import React, { useState, useEffect, useMemo } from 'react';
import { BASE_API } from '../axios';
import { CopyOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { makePostCall, getUserDetails, calculateExactAge, STUDY_DATE_FORMAT } from '../utils/helper';
import { CENTER_NAME } from '../constants/APP_CONSTANTS';
import moment from 'moment';

function PdfViewer({
  pdfArrayBuffer,
  patient_id,
  order_id,
  patientData,
  onReportUpdated,
  refreshing = false,
  hideCopyButton = false,
  hideWhatsappButton = false,
  mimeType = 'application/pdf',
  showNativeToolbar = false,
}) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [key, setKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  const [linkGenerating, setLinkGenerating] = useState(false);

  useEffect(() => {
    setShareableLink('');
    setCopied(false);
  }, [order_id, patient_id]);

  // Original direct report URL (for fallback)
  const reportUrl = useMemo(() => {
    return `${BASE_API}/print-pat-report?order_id=${order_id}&patient_id=${patient_id}`;
  }, [patient_id, order_id]);

  useEffect(() => {
    if (pdfArrayBuffer) {
      // Convert ArrayBuffer to Blob
      const blob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });

      // Create a Blob URL
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      // Force refresh after URL is created
      setTimeout(() => {
        setKey(prev => prev + 1);
      }, 100);

      // Clean up the URL when component unmounts
      return () => URL.revokeObjectURL(url);
    }
  }, [pdfArrayBuffer]);

  if (!pdfUrl) {
    return <div>Loading PDF...</div>;
  }

  // Generate shareable link on-demand
  const generateShareableLink = async () => {
    if (shareableLink) {
      return shareableLink; // Already generated for this patient, reuse it
    }

    setLinkGenerating(true);
    try {
      const response = await makePostCall('/generate-shareable-link', {
        order_id: order_id,
        user_id: getUserDetails()?.username,
        expires_in_hours: 168, // 7 days
      });

      if (response.data.success) {
        const generatedLink = response.data.data.url;
        setShareableLink(generatedLink);
        return generatedLink;
      } else {
        message.error(`Failed to generate link: ${response.data.message || 'Unknown error'}`);
        return reportUrl; // Fallback to direct URL
      }
    } catch (error) {
      console.error('Error generating shareable link:', error);
      message.error(`Error: ${error.response?.data?.message || error.message || 'Network error'}`);
      return reportUrl; // Fallback to direct URL
    } finally {
      setLinkGenerating(false);
    }
  };

  // Copy button now generates link on-demand
  const copyToClipBoard = async () => {
    if (linkGenerating) {
      message.warning('Please wait, generating shareable link...');
      return;
    }

    try {
      const linkToShare = await generateShareableLink();
      await navigator.clipboard.writeText(linkToShare);
      setCopied(true);

      if (linkToShare === reportUrl) {
        message.success('Direct report link copied!');
      } else {
        message.success('WhatsApp-friendly link copied!');
      }

      setTimeout(() => {
        setCopied(false);
      }, 3000);
    } catch (error) {
      message.error('Failed to copy link');
    }
  };

  const checkWhatsAppDesktopAndSend = message_text => {
    const whatsappDesktopUrl = `whatsapp://send?text=${message_text}`;
    const whatsappWebUrl = `https://web.whatsapp.com/send?text=${message_text}`;

    // Try to open WhatsApp desktop app
    const link = document.createElement('a');
    link.href = whatsappDesktopUrl;
    link.click();

    // Wait a short time and then check if WhatsApp desktop opened
    setTimeout(() => {
      // If the page is still focused, it means WhatsApp desktop didn't open
      if (document.hasFocus()) {
        window.open(whatsappWebUrl, '_blank');
        message.success('Report shared to WhatsApp Web!');
      } else {
        message.success('Report shared to WhatsApp desktop!');
      }
    }, 500);
  };

  // WhatsApp share now generates link on-demand
  const shareOnWhatsApp = async () => {
    if (linkGenerating) {
      message.warning('Please wait, generating shareable link...');
      return;
    }

    try {
      const linkToShare = await generateShareableLink();

      // Format message for report sharing
      let formattedMessage = 'Medical Report - Please view:';

      if (patientData) {
        const patient = patientData?.pacs_order?.patient;
        const ps_study_dt_tm = patientData?.ps_study_dt_tm;
        const patientAge = patient?.pat_dob ? calculateExactAge(patient.pat_dob) : null;
        const patientSex = patient?.pat_sex || null;
        const ageSexPart = patientAge || patientSex ? `(${[patientAge, patientSex].filter(Boolean).join('/')})` : null;
        const patientInfo = [patient?.pat_name, ageSexPart].filter(Boolean).join(' ');
        const formattedDate = ps_study_dt_tm
          ? moment(ps_study_dt_tm).format(STUDY_DATE_FORMAT)
          : 'Unknown date';

        formattedMessage = [
          'Dear Sir/Mam,',
          '',
          `${CENTER_NAME} have shared your medical report for ${patientInfo || 'the patient'} dated ${formattedDate} via Photon PACS`,
          '',
          'Click on link to view your report:',
          '',
          linkToShare,
        ].join('\n');
      } else {
        formattedMessage = `Medical Report - Please view: ${linkToShare}`;
      }

      const message_text = encodeURIComponent(formattedMessage);
      checkWhatsAppDesktopAndSend(message_text);
    } catch (error) {
      message.error('Failed to generate shareable link');
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Quick action buttons */}
      <div
        style={{
          padding: '8px 12px',
          display: 'flex',
          gap: '5px',
          flexWrap: 'wrap',
          alignItems: 'center',
          borderBottom: '1px solid #e8e8e8',
          backgroundColor: '#fafafa',
          flexShrink: 0,
        }}
      >
        {/* Copy Link Button - now generates on-demand */}
        {!hideCopyButton && (
          <button
            style={{
              padding: '5px 10px',
              fontSize: '12px',
              backgroundColor: linkGenerating ? '#ccc' : 'rgb(134 155 173)',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: linkGenerating ? 'not-allowed' : 'pointer',
              opacity: linkGenerating ? 0.6 : 1,
            }}
            onClick={copyToClipBoard}
            title={linkGenerating ? 'Generating shareable link...' : 'Copy shareable report URL'}
            disabled={linkGenerating}
          >
            {linkGenerating ? '⏳' : <CopyOutlined />}
          </button>
        )}

        {/* WhatsApp Share Button - now generates on-demand */}
        {!hideWhatsappButton && (
          <button
            style={{
              padding: '5px 10px',
              fontSize: '12px',
              backgroundColor: linkGenerating ? '#ccc' : '#25D366',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: linkGenerating ? 'not-allowed' : 'pointer',
              opacity: linkGenerating ? 0.6 : 1,
            }}
            onClick={shareOnWhatsApp}
            disabled={linkGenerating}
            title={linkGenerating ? 'Generating shareable link...' : 'Share on WhatsApp'}
          >
            {linkGenerating ? '⏳' : <WhatsAppOutlined />}
          </button>
        )}

        {/* Status Messages */}
        {copied && (
          <span
            style={{ color: 'green', fontSize: '11px', fontWeight: 'bold' }}
            className="success-text"
          >
            Link copied!
          </span>
        )}

        {linkGenerating && (
          <span style={{ color: '#666', fontSize: '11px', padding: '2px 5px' }}>
            Generating link...
          </span>
        )}

        {shareableLink && !linkGenerating && (
          <span
            style={{ color: '#25D366', fontSize: '11px', fontWeight: 'bold', padding: '2px 5px' }}
          >
            ✓ Ready to share
          </span>
        )}
      </div>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: 'white',
          position: 'relative',
        }}
      >
        {/* Loading overlay when refreshing PDF */}
        {refreshing && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <div
              style={{
                fontSize: '24px',
                animation: 'spin 1s linear infinite',
              }}
            >
              ⏳
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>Refreshing PDF...</div>
            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {/* PDF View Mode */}
        <object
          key={key}
          data={
            showNativeToolbar
              ? pdfUrl
              : `${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=100`
          }
          type={mimeType}
          width="100%"
          height="100%"
          style={{
            border: 'none',
            minHeight: '100%',
          }}
        >
          <p style={{ padding: '20px', textAlign: 'center' }}>
            Your browser does not support PDFs.
            <br />
            <a
              href={pdfUrl}
              download="report.pdf"
              style={{ color: '#1890ff', textDecoration: 'underline' }}
            >
              Download the PDF
            </a>{' '}
            instead.
          </p>
        </object>
      </div>
    </div>
  );
}

export default PdfViewer;
